import i18nComponentsXpsInstance from '@cohort/components-xps/lib/i18n';
import type {WebappLanguage} from '@cohort/shared/schema/common';
import {formatI18nLanguage} from '@cohort/shared/utils/localization';
import {Apps} from '@cohort/wallet/apps';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import {initI18n} from '@cohort/wallet/lib/i18n';
import {useQueryClient} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {changeLanguage} from 'i18next';
import {useCallback, useEffect} from 'react';
import {getI18n} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {shallow} from 'zustand/shallow';

const DEFAULT_LANGUAGE = 'en';

export const useLocalization = (): void => {
  const merchant = useMerchantContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {user, isAuthenticating} = useUserStore(
    store => ({
      user: store.user,
      isAuthenticating: store.isAuthenticating,
    }),
    shallow
  );
  const {localizationLoaded, setLocalizationLoaded} = useUserStore(
    store => ({
      localizationLoaded: store.localizationLoaded,
      setLocalizationLoaded: store.setLocalizationLoaded,
    }),
    shallow
  );

  const setLanguage = useCallback(
    async (language: string): Promise<void> => {
      const customTerms = merchant.customTerms[language as WebappLanguage];
      const translationResource = (await import(`../lib/locales/${language}.json`)).default;

      // Load global translations
      Object.keys(translationResource).forEach(ns => {
        getI18n().addResourceBundle(language, ns, translationResource[ns]);
      });
      const appTranslationResources = await Promise.all(
        Apps.map(app => import(`../apps/${app.spec.id}/locales/${language}.json`))
      );

      // Load app translations
      appTranslationResources.forEach((appTranslationResource, index) => {
        const ns = `app-${Apps[index]?.spec.id}`;

        getI18n().addResourceBundle(language, ns, appTranslationResource.default);
      });

      // Load custom terms
      // i18nOwl-ignore [common.badges, common.digitalAssets, common.experienceSpace, common.home, common.journeys, common.perks, common.rewards]
      if (customTerms !== undefined) {
        getI18n().addResourceBundle(language, 'common', customTerms, true, true);

        if (customTerms['experienceSpace'] !== undefined) {
          getI18n().addResourceBundle(
            language,
            'layouts',
            {
              space: {
                spaceLayout: {
                  experienceSpaceTitle: customTerms['experienceSpace'],
                },
              },
            },
            true,
            true
          );
        }
      }
      // Handle merchant and test merchant
      const merchantName = merchant.slug.replace(/-test$/u, '');
      const res = await fetch(
        `https://storage.googleapis.com/${
          import.meta.env.COHORT_FIREBASE_STORAGE_BUCKET
        }/translations/${merchantName}/${language}.json`
      );

      if (res.ok) {
        const customTranslations = await res.json();

        Object.keys(customTranslations).forEach(ns => {
          getI18n().addResourceBundle(language, ns, customTranslations[ns], true, true);
        });
      }
      if (language !== getI18n().language || language !== i18nComponentsXpsInstance.language) {
        dayjs.locale(language);
        changeLanguage(language);
        i18nComponentsXpsInstance.changeLanguage(language);
        queryClient.invalidateQueries();
        localStorage.setItem('lng-wallet', language);
      }
      setLocalizationLoaded(true);
    },
    [queryClient, merchant.customTerms, merchant.slug, setLocalizationLoaded]
  );

  useEffect(() => {
    // fallback language should be merchant default only if english is not supported
    if (!merchant.supportedLanguages.some(lang => lang === DEFAULT_LANGUAGE)) {
      initI18n({fallbackLng: merchant.defaultLanguage});
      i18nComponentsXpsInstance.init({fallbackLng: merchant.defaultLanguage});
      i18nComponentsXpsInstance.changeLanguage(merchant.defaultLanguage);
    }
  }, [merchant]);

  useEffect(() => {
    // Wait for authentication to finish to ensure priority 0 is respected
    if (isAuthenticating) {
      return;
    }
    const langParam = searchParams.get('lng');

    if (langParam !== null) {
      searchParams.delete('lng');
      setSearchParams(searchParams, {replace: true});
    }

    // priority 0: user logged in
    if (user) {
      setLanguage(user.lang);
      return;
    }

    if (localizationLoaded) {
      return;
    }

    // priority 1: lng param
    if (langParam && merchant.supportedLanguages.some(lang => lang === langParam)) {
      setLanguage(langParam);
      return;
    }

    // priority 2: storage
    const storageLang = localStorage.getItem('lng-wallet');
    if (storageLang !== null && merchant.supportedLanguages.some(lang => lang === storageLang)) {
      setLanguage(storageLang);
      return;
    }

    // priority 3: navigator settings
    const browserLanguage = formatI18nLanguage(navigator.language);
    if (merchant.supportedLanguages.some(lang => lang === browserLanguage)) {
      setLanguage(browserLanguage);
      return;
    }

    // priority 4: english
    if (merchant.supportedLanguages.some(lang => lang === DEFAULT_LANGUAGE)) {
      setLanguage(DEFAULT_LANGUAGE);
      return;
    }

    // priority 5: merchant default
    setLanguage(merchant.defaultLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Do not depend on searchParams.
  }, [merchant, setLanguage, user, isAuthenticating]);
};
